import { useRef, useState } from "react";
import React from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import emailjs from "emailjs-com";
import { Typography, Grid, Box } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Contact.css";
import { EMAILJS, PRACTITIONER, IMAGES, SOCIALS, BOOKING, OFFICE } from "../../config/site";
import FlipCard from "../utils/FlipCard/FlipCard";
import {
  Calendar2,
  Whatsapp,
  GeoAlt,
  Linkedin,
  Instagram,
  Facebook,
  ArrowRepeat,
} from "react-bootstrap-icons";

/** Délai minimal entre deux envois (anti-spam), en millisecondes */
const SUBMIT_COOLDOWN_MS = 30_000;
/** Longueur maximale acceptée pour un message */
const MESSAGE_MAX_LENGTH = 2000;

function Contact() {
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  /** Champ « honeypot » invisible : rempli uniquement par les robots */
  const [website, setWebsite] = useState("");
  /** Horodatage du dernier envoi réussi (limitation de débit) */
  const lastSubmitRef = useRef<number>(0);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 1) Honeypot : si ce champ caché est rempli, c'est un robot → on ignore
    //    silencieusement (on simule un succès pour ne pas informer le bot).
    if (website.trim() !== "") {
      setEmail("");
      setSubject("");
      setMessage("");
      return;
    }

    // 2) Limitation de débit côté client contre les envois en rafale.
    const now = Date.now();
    if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
      const wait = Math.ceil(
        (SUBMIT_COOLDOWN_MS - (now - lastSubmitRef.current)) / 1000
      );
      toast.info(
        `Merci de patienter ${wait}s avant d'envoyer un nouveau message.`
      );
      return;
    }

    // 3) Validation renforcée du contenu.
    if (message.trim().length === 0 || message.length > MESSAGE_MAX_LENGTH) {
      toast.error(
        `Votre message doit contenir entre 1 et ${MESSAGE_MAX_LENGTH} caractères.`
      );
      return;
    }

    if (isSending) return;
    setIsSending(true);

    const templateParams = {
      from_name: email,
      to_name: PRACTITIONER.name,
      subject: subject,
      message: message,
    };

    emailjs
      .send(EMAILJS.serviceId, EMAILJS.templateId, templateParams, EMAILJS.userId)
      .then(() => {
        lastSubmitRef.current = Date.now();
        toast.success(
          "E-mail envoyé avec succès, une réponse vous sera donnée dans les plus brefs délais."
        );
        setEmail("");
        setSubject("");
        setMessage("");
      })
      .catch(() => {
        toast.error(
          "Échec de l'envoi de l'e-mail, je vous prie d'utiliser ma carte de visite pour me contacter."
        );
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <section id="contact" className="contact-section">
      <Container>
        <Box sx={{ textAlign: "center", mb: 7 }}>
          <span className="luxe-eyebrow">Prenons contact</span>
          <Typography
            variant="h3"
            className="luxe-heading"
            sx={{ fontWeight: 700, mb: 2, letterSpacing: "-0.02em" }}
          >
            Contactez-moi
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "var(--luxe-muted)",
              maxWidth: "600px",
              margin: "0 auto",
              fontSize: "1.1rem",
              lineHeight: 1.7,
              fontWeight: 300,
            }}
          >
            N&apos;hésitez pas à me contacter pour toute question ou pour
            prendre rendez-vous.
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Form onSubmit={handleSubmit} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Form.Group className="mb-3" controlId="formEmail">
                <Form.Label>Votre e-mail</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formSubject">
                <Form.Label>Objet</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Email subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formMessage">
                <Form.Label>Votre message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={MESSAGE_MAX_LENGTH}
                  required
                />
                <Form.Text
                  className="text-muted"
                  style={{ marginTop: "8px", display: "block" }}
                >
                  Votre adresse email sera réutilisée dans un seul cas : vous
                  recontacter.
                </Form.Text>
              </Form.Group>

              {/* Information RGPD */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "var(--luxe-ivory)",
                  borderRadius: "10px",
                  borderLeft: "3px solid var(--luxe-gold)",
                  border: "1px solid rgba(201, 169, 106, 0.25)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--luxe-muted)",
                    fontSize: "0.8rem",
                    lineHeight: 1.55,
                    display: "block",
                  }}
                >
                  <strong>Protection des données personnelles :</strong>{" "}
                  Conformément au Règlement Général sur la Protection des
                  Données (RGPD) et à la loi Informatique et Libertés du 6
                  janvier 1978 modifiée, vous disposez d&apos;un droit
                  d&apos;accès, de rectification, d&apos;effacement, de
                  portabilité, de limitation du traitement et d&apos;opposition
                  au traitement de vos données personnelles. Vos données sont
                  collectées uniquement dans le but de traiter votre demande de
                  contact et ne seront en aucun cas transmises à des tiers. Pour
                  exercer vos droits, vous pouvez me contacter directement.
                </Typography>
              </Box>

              {/* Champ honeypot anti-spam : masqué aux humains, piège à robots.
                  Non focusable et exclu de l'accessibilité. */}
              <Form.Control
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                }}
              />

              <Button
                variant="primary"
                type="submit"
                disabled={isSending}
                style={{ marginTop: "auto", alignSelf: "flex-start" }}
              >
                {isSending ? "Envoi en cours…" : "Envoyer"}
              </Button>
            </Form>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Carte de visite + indice de retournement */}
            <div className="contact-card-wrap">
              <FlipCard
                frontSrc={IMAGES.visitCardFront}
                backSrc={IMAGES.visitCardBack}
                alt="Carte de visite de Nawel BOUCHOUKH, psychologue clinicienne"
              />
              <span className="flip-hint">
                <ArrowRepeat /> Survolez la carte pour découvrir le verso
              </span>
            </div>

            {/* Coordonnées rapides */}
            <div className="contact-quick">
              <h4 className="contact-quick-title">Me joindre directement</h4>

              <a
                className="contact-quick-item"
                href={BOOKING.doctolib}
                target="_blank"
                rel="noreferrer"
              >
                <span className="cqi-icon">
                  <Calendar2 />
                </span>
                <span className="cqi-text">
                  <strong>Prendre rendez-vous</strong>
                  <span>En cabinet, à domicile ou en téléconsultation via Doctolib</span>
                </span>
              </a>

              <a
                className="contact-quick-item"
                href={SOCIALS.whatsapp}
                target="_blank"
                rel="noreferrer"
              >
                <span className="cqi-icon">
                  <Whatsapp />
                </span>
                <span className="cqi-text">
                  <strong>WhatsApp</strong>
                  <span>Pour une question rapide</span>
                </span>
              </a>

              <div className="contact-quick-item contact-quick-item--static">
                <span className="cqi-icon">
                  <GeoAlt />
                </span>
                <span className="cqi-text">
                  <strong>Le cabinet</strong>
                  <span>{OFFICE.addressLine}</span>
                </span>
              </div>

              <div className="contact-socials">
                <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                  <Linkedin />
                </a>
                <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
                  <Instagram />
                </a>
                <a href={SOCIALS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
                  <Facebook />
                </a>
              </div>
            </div>
          </Grid>
        </Grid>
        <ToastContainer
          position="bottom-left"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Container>
    </section>
  );
}

export default Contact;
